import styled, {css} from 'styled-components';
import {getColorFromTheme} from '../../../../../../ui/ui-utils';

const buttonReset = css`
  border: 0;
  font-family: Graphik, sans-serif;
  cursor: pointer;
`;

export const Field = styled.label`
  display: block;
  margin-bottom: 18px;
  color: ${getColorFromTheme('blackTwo')};
  font: 500 13px/20px Graphik, sans-serif;
`;

export const TextInput = styled.input`
  width: 100%;
  height: 44px;
  margin-top: 7px;
  padding: 10px 12px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  outline: none;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('blackTwo')};
  font: 400 14px/22px Graphik, sans-serif;

  &::placeholder {
    color: ${getColorFromTheme('textBody')};
  }

  &:focus {
    border-color: ${getColorFromTheme('purple300')};
    box-shadow: 0 0 0 3px rgba(88, 90, 223, 0.12);
  }
`;

export const TypeLabel = styled.span`
  display: block;
  margin-bottom: 7px;
  color: ${getColorFromTheme('blackTwo')};
  font: 500 13px/20px Graphik, sans-serif;
`;

export const TypeOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
`;

export const TypeOption = styled.button<{$selected: boolean}>`
  ${buttonReset};
  display: flex;
  min-height: 54px;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border: 1px solid
    ${({$selected}) =>
      $selected
        ? getColorFromTheme('purple300')
        : getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${({$selected}) =>
    $selected
      ? getColorFromTheme('backgroundLight')
      : getColorFromTheme('white')};
  color: ${getColorFromTheme('blackTwo')};
  font: 500 14px/22px Graphik, sans-serif;

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 2px;
  }
`;

export const SelectionMark = styled.span<{$selected: boolean}>`
  display: inline-flex;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border: 1px solid
    ${({$selected}) =>
      $selected
        ? getColorFromTheme('purple300')
        : getColorFromTheme('borderDark')};
  border-radius: 4px;
  background: ${({$selected}) =>
    $selected ? getColorFromTheme('purple300') : getColorFromTheme('white')};
  color: ${getColorFromTheme('alwaysWhite')};
  font-size: 12px;
`;

export const SelectWrap = styled.div`
  position: relative;
`;

export const SelectTrigger = styled.button<{$open?: boolean}>`
  ${buttonReset};
  display: flex;
  width: 100%;
  min-height: 46px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  border: 1px solid
    ${({$open}) =>
      $open ? getColorFromTheme('purple300') : getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('blackTwo')};
  font: 400 14px/22px Graphik, sans-serif;
  text-align: left;

  svg {
    flex: 0 0 auto;
    stroke: ${getColorFromTheme('textBody')};
    transform: rotate(${({$open}) => ($open ? '180deg' : '0deg')});
  }

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 2px;
  }
`;

export const LeadSummary = styled.span`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 9px;
`;

export const LeadName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SelectMenu = styled.div`
  position: absolute;
  z-index: 30;
  top: calc(100% + 6px);
  right: 0;
  left: 0;
  max-height: 250px;
  padding: 6px;
  overflow-y: auto;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  box-shadow: ${({theme}) => theme.elevation.level2};
`;

export const SelectOption = styled.button<{$selected?: boolean}>`
  ${buttonReset};
  display: flex;
  width: 100%;
  min-height: 44px;
  align-items: center;
  gap: 10px;
  padding: 7px 9px;
  border-radius: 6px;
  background: ${({$selected}) =>
    $selected
      ? getColorFromTheme('backgroundDark')
      : getColorFromTheme('white')};
  color: ${({$selected}) =>
    $selected ? getColorFromTheme('purple300') : getColorFromTheme('blackTwo')};
  font: 400 14px/22px Graphik, sans-serif;
  text-align: left;

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${getColorFromTheme('backgroundDark')};
  }
`;

export const DangerZone = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;

  @media (max-width: 480px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const DangerCopy = styled.div`
  min-width: 0;

  strong {
    display: block;
    margin-bottom: 3px;
    color: ${getColorFromTheme('blackTwo')};
    font: 600 14px/22px Graphik, sans-serif;
  }

  span {
    display: block;
    color: ${getColorFromTheme('textBody')};
    font: 400 12px/19px Graphik, sans-serif;
  }
`;

export const ConfirmCopy = styled.p`
  margin: 0;
  color: ${getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;

  strong {
    color: ${getColorFromTheme('blackTwo')};
  }
`;

export const ErrorCopy = styled.p`
  margin: 8px 0 0;
  color: ${getColorFromTheme('red400')};
  font: 400 12px/18px Graphik, sans-serif;
`;
